import { useState, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { taskFromDb, taskToDb } from '@/lib/mappers'

export function useTasks(activeTermId, userId) {
  const [tasks, setTasks] = useState([])

  async function loadTasks(termId) {
    const { data } = await supabase.from('tasks').select('*').eq('term_id', termId)
    const mapped = (data || []).map(taskFromDb)
    setTasks(mapped)
    return mapped
  }

  const setTasksWithSync = useCallback((updaterOrTasks) => {
    setTasks(prev => {
      const next = typeof updaterOrTasks === 'function' ? updaterOrTasks(prev) : updaterOrTasks
      const prevIds = new Set(prev.map(t => t.id))
      const added = next.filter(t => !prevIds.has(t.id))
      if (added.length > 0 && activeTermId) {
        const dbRows = added.map(t => taskToDb(t, activeTermId, userId))
        supabase.from('tasks').upsert(dbRows).then(({ error }) => {
          if (error) console.error('Failed to sync tasks:', error)
        })
      }
      return next
    })
  }, [activeTermId, userId])

  // Persist a task's subtasks array to the DB, scoped to the active term.
  // Compute `subtasks` synchronously from current state BEFORE calling this —
  // reading a value assigned inside a setTasks() updater is unreliable because
  // React does not run the updater synchronously, which silently skips the write.
  async function persistSubtasks(taskId, subtasks) {
    if (!activeTermId) return
    await supabase.from('tasks')
      .update({ subtasks })
      .eq('id', taskId)
      .eq('term_id', activeTermId)
  }

  async function toggleSubtask(taskId, subtaskId) {
    const task = tasks.find(t => t.id === taskId)
    if (!task) return
    const subtasks = task.subtasks.map(st =>
      st.id === subtaskId ? { ...st, done: !st.done } : st
    )
    setTasks(prev => prev.map(t => (t.id === taskId ? { ...t, subtasks } : t)))
    await persistSubtasks(taskId, subtasks)
  }

  async function deleteTask(taskId) {
    setTasks(prev => prev.filter(t => t.id !== taskId))
    if (activeTermId) {
      await supabase.from('tasks').delete().eq('id', taskId).eq('term_id', activeTermId)
    }
  }

  async function updateSubtask(taskId, subtaskId, updates) {
    const task = tasks.find(t => t.id === taskId)
    if (!task) return
    const subtasks = task.subtasks.map(st =>
      st.id === subtaskId ? { ...st, ...updates } : st
    )
    setTasks(prev => prev.map(t => (t.id === taskId ? { ...t, subtasks } : t)))
    await persistSubtasks(taskId, subtasks)
  }

  async function addSubtask(taskId, subtask) {
    const task = tasks.find(t => t.id === taskId)
    if (!task) return
    const subtasks = [...task.subtasks, subtask]
    setTasks(prev => prev.map(t => (t.id === taskId ? { ...t, subtasks } : t)))
    await persistSubtasks(taskId, subtasks)
  }

  async function deleteSubtask(taskId, subtaskId) {
    const task = tasks.find(t => t.id === taskId)
    if (!task) return
    const subtasks = task.subtasks.filter(st => st.id !== subtaskId)
    setTasks(prev => prev.map(t => (t.id === taskId ? { ...t, subtasks } : t)))
    await persistSubtasks(taskId, subtasks)
  }

  async function reorderSubtasks(taskId, fromIndex, toIndex) {
    const task = tasks.find(t => t.id === taskId)
    if (!task) return
    const subtasks = [...task.subtasks]
    const [moved] = subtasks.splice(fromIndex, 1)
    subtasks.splice(toIndex, 0, moved)
    setTasks(prev => prev.map(t => (t.id === taskId ? { ...t, subtasks } : t)))
    await persistSubtasks(taskId, subtasks)
  }

  async function updateTask(taskId, updates) {
    setTasks(prev => prev.map(task =>
      task.id === taskId ? { ...task, ...updates } : task
    ))
    if (activeTermId) {
      const dbUpdates = {}
      if (updates.dueDate !== undefined) dbUpdates.due_date = updates.dueDate
      if (updates.priority !== undefined) dbUpdates.priority = updates.priority
      if (updates.title !== undefined) dbUpdates.title = updates.title
      await supabase.from('tasks').update(dbUpdates).eq('id', taskId).eq('term_id', activeTermId)
    }
  }

  async function replaceSubtasks(taskId, newSubtasks) {
    const task = tasks.find(t => t.id === taskId)
    if (!task) return
    const doneMap = {}
    task.subtasks.forEach(st => { doneMap[st.title.toLowerCase()] = st.done })
    const subtasks = newSubtasks.map((st) => ({
      id: `${taskId}-${crypto.randomUUID().slice(0, 8)}`,
      title: st.title,
      timeEstimate: st.timeEstimate,
      done: doneMap[st.title.toLowerCase()] || false,
    }))
    setTasks(prev => prev.map(t => (t.id === taskId ? { ...t, subtasks } : t)))
    await persistSubtasks(taskId, subtasks)
  }

  return {
    tasks,
    setTasks: setTasksWithSync,
    _setTasksRaw: setTasks,
    loadTasks,
    toggleSubtask,
    deleteTask,
    updateTask,
    updateSubtask,
    addSubtask,
    deleteSubtask,
    reorderSubtasks,
    replaceSubtasks,
  }
}
